/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 * for rdpvcbridge vchan transport handling
 *
 * @format
 */

export const VVC_CONN_DUMMY_CHAN_NAME = "VVC_CONN_DUMMY_CHAN*";
export const RDP_STREAM_PREFIX = "RDP__*";
export const INTERNAL_STREAM_PREFIX = "VMWARE__*";
export const HZN_STREAM_PREFIX = "HZN__*";

import { CHANNEL_STATE } from "../utils/vvc-commen";
import { vcTransportMsgSchema } from "../definitions/vc-transport-message.schema";
import { VCTransportMsg } from "./transpert-message";
import { ProtocolUtil } from "../../vdpservice/util/protocol-util";
import { ProtocolHelper } from "../../vdpservice/util/protocol-helper";
import { VCWireMsgType } from "./rdpvcbridge.const";
import { VcClient } from "./vc-client";
import Logger from "../../../../core/libs/logger";
import { Subject } from "rxjs";

abstract class VCBaseChannel {
   private vvcListener;
   private HZN_vvcListener;
   /**
    * Do not change the params for the class, hardcoded for now, it's easy to change it later
    * We just to change INTERNAL_STREAM_PREFIX = "HZN__*";
    * Then remove HZN_channelPrefix, HZN_vvcListener
    * */
   private HZN_channelPrefix = HZN_STREAM_PREFIX;
   private channelhelper: ProtocolHelper;
   private channelMap = new Map<number, any>();
   protected logger = new Logger(Logger.RDPVCBRIDGE, null, Logger.Modules.RDPVCBridge);
   constructor(
      protected sessionKey: string,
      vvc,
      private vvcSession,
      private vcClient: VcClient,
      private channelPrefix: string,
      private channelType: string,
      private supportedMessage: Array<VCWireMsgType>,
      private postChannelOpened?: (channel: any) => void,
      private postChannelClosed?: (channel: any) => void
   ) {
      this.channelhelper = ProtocolUtil.getHelper(vcTransportMsgSchema);
      if (!this.vvcListener) {
         this.vvcListener = vvc.createListener(vvcSession, this.channelPrefix);
         if (this.vvcListener) {
            this.vvcListener.onpeeropen = this.onPeerOpen;
         }
      }
      if (this.channelPrefix === INTERNAL_STREAM_PREFIX && !this.HZN_vvcListener) {
         this.HZN_vvcListener = vvc.createListener(vvcSession, this.HZN_channelPrefix);
         if (this.HZN_vvcListener) {
            this.HZN_vvcListener.onpeeropen = this.onPeerOpen;
         }
      }
   }

   private onPeerOpen = (session: any, channel: any): void => {
      this.logger.debug(channel.id + "   " + session);
      if (this.vvcSession !== session) {
         this.logger.error("Unexpected session object.");
         return;
      }
      // Accept the channel.
      if (!this.vvcSession.acceptChannel(this.vvcSession.getChannel(channel.id))) {
         this.logger.error(
            "vvcSession.acceptChannel failed for " + this.channelType + " channel with id: " + channel.id
         );
         return;
      }
      if (typeof this.postChannelOpened === "function") {
         this.postChannelOpened(channel);
      }
      channel.onclose = () => {
         this.logger.info("close a " + this.channelType + " channel");
         this.channelMap.delete(channel.id);
         if (typeof this.postChannelClosed === "function") {
            this.postChannelClosed(channel);
         }
      };
      const channelOnMessage = this.getResponseFunction(channel.id);
      channel.onmessage = (e) => {
         this.logger.debug(this.channelType + " channel get a message");
         const messages = this.parseMessage(e);
         try {
            this.logger.debug("rdpvc message number in one vvc event:" + messages.length);
         } catch (e) {
            this.logger.error("unable to print rdpvc message");
         }
         messages.forEach((message) => {
            this.vcClient.onMessage(message, channelOnMessage, this.sessionKey);
         });
      };
      // An event listener to called when there is a VVC channel error.
      channel.onerror = () => {
         this.logger.info("error in a " + this.channelType + " channel");
         this.channelMap.delete(channel.id);
         if (typeof this.postChannelClosed === "function") {
            this.postChannelClosed(channel);
         }
      };
      this.channelMap.set(channel.id, channel);

      this.logger.info("get a " + this.channelType + " channel");
   };

   private getResponseFunction = (channelId: number) => {
      return (response, castConfig) => {
         this.sendResponse(response, castConfig, channelId);
      };
   };

   public sendResponse = (response, castConfig, channelId: number) => {
      const responseStream = this.channelhelper.simplifiedStreamify("VCWireMsg", response, castConfig);
      this.logger.trace("send response on " + this.channelType);
      this.logger.dump("response object: " + JSON.stringify(response));
      if (!this.channelMap.has(channelId)) {
         this.logger.warning("invalid channel id to send response to " + channelId);
         return;
      }
      this.channelMap.get(channelId).send(responseStream);
   };

   private isExpectedType = (type) => {
      return this.supportedMessage.includes(type);
   };

   private getCastOption = (type) => {
      switch (type) {
         case VCWireMsgType.WireMsgSessionInfoQuery: {
            return "SessionInfoQuery";
         }
         case VCWireMsgType.WireMsgSessionInfoResponse: {
            this.logger.warning("not expect to get WireMsgSessionInfoResponse at client");
            return "SessionInfoResponse";
         }
         case VCWireMsgType.WireMsgData: {
            return "Data";
         }
         case VCWireMsgType.WireMsgNone:
         default:
            this.logger.error("unexpected type");
            return "None";
      }
   };

   private parseMessage = (e) => {
      this.logger.trace("parsing " + this.channelType + " channel message");

      const reader = ProtocolUtil.getReader(new Uint8Array(e.data), false, true);
      const castConfig = {
         payload: (parsed) => {
            if (!parsed || !parsed.header) {
               this.logger.error("internal error, no header found in package");
               return "None";
            }
            const messageTypeEnum = parsed.header.msgType;
            if (!(messageTypeEnum >= 0 || messageTypeEnum < VCWireMsgType.WireMsgMax)) {
               this.logger.error("internal error, invalid message type found in package");
               return "None";
            }
            const messageTypeStr = VCTransportMsg.MsgTypeStr(messageTypeEnum);
            this.logger.debug(
               "get message on " + this.channelType + " channel of " + messageTypeStr + " (" + messageTypeEnum + ")"
            );
            if (!this.isExpectedType(messageTypeEnum)) {
               this.logger.warning("Not expect to get message of " + messageTypeStr);
               return "None";
            } else {
               return this.getCastOption(messageTypeEnum);
            }
         }
      };
      const requests = [];

      do {
         try {
            requests.push(this.channelhelper.parse(reader, "VCWireMsg", castConfig));
         } catch (e) {
            Logger.error("unexpected error found when paring message on vchan");
            Logger.exception(e);
            break;
         }
      } while (reader.hasRemainStream());
      if (requests.length >= 2) {
         Logger.debug("split combined message into " + requests.length + " requests");
      }
      return requests;
   };

   protected _sendData = (dataPackage, channelId: number) => {
      const castConfig = {
         payload: "Data"
      };
      if (!dataPackage) {
         return;
      }
      const dataStream = this.channelhelper.simplifiedStreamify("VCWireMsg", dataPackage, castConfig);
      this.logger.debug("send data on " + this.channelType);
      if (!this.channelMap.has(channelId)) {
         this.logger.warning("invalid channelId when send message");
      }
      this.channelMap.get(channelId).send(dataStream);
   };
}

export class VirtualChanInfo {
   constructor(
      public channelName: string,
      public channelId: number,
      private rdpChan: RDPChan
   ) {}
   public sendData = (dataPackage: any) => {
      this.rdpChan.sendData(dataPackage, this.channelName);
   };
}

// TODO: refactor this file later.
export class RDPChan extends VCBaseChannel {
   public channelNameMap = new Map<string, number>();
   public static $registerChannel = new Subject<VirtualChanInfo>();
   public static $unregisterChannel = new Subject<string>();
   constructor(sessionKey, vvc, vvcSession, vcClient) {
      super(
         sessionKey,
         vvc,
         vvcSession,
         vcClient,
         RDP_STREAM_PREFIX,
         "Stream",
         [VCWireMsgType.WireMsgData],
         (channel) => {
            if (!channel) {
               this.logger.error("invalid stream channel");
               return;
            }
            const channelName = this.getChannelName(channel);
            const channelId = channel.id;

            if (this.channelNameMap.has(channelName)) {
               this.logger.warning("unexpected dup channel to " + channelName + " by new vvc " + channel.name);
               this.logger.warning("previous stream channel discarded");
            }
            this.channelNameMap.set(channelName, channelId);
            this.logger.info("establish vchan " + channelName);
            RDPChan.$registerChannel.next(new VirtualChanInfo(channelName, channelId, this));
         },
         (channel) => {
            const channelName = this.getChannelName(channel);
            this.channelNameMap.delete(channelName);
            RDPChan.$unregisterChannel.next(channelName);
         }
      );
   }

   private getChannelName = (channel): string => {
      const channelName = channel.name.replace("RDP__", "").replaceAll("\0", ""); //remove the \0 from string
      return channelName;
   };

   public hostedBySession = (sessionKey: string) => {
      return sessionKey === this.sessionKey;
   };

   public sendData = (dataPackage: any, channelName: string) => {
      if (!this.channelNameMap.has(channelName)) {
         Logger.error("failed to send data for vchan name of " + channelName);
         return;
      }
      this._sendData(dataPackage, this.channelNameMap.get(channelName));
   };
}

export class InternalChan extends VCBaseChannel {
   constructor(sessionKey, vvc, vvcSession, vcClient) {
      super(sessionKey, vvc, vvcSession, vcClient, INTERNAL_STREAM_PREFIX, "Internal", [
         VCWireMsgType.WireMsgSessionInfoQuery
      ]);
   }
}

export class DummyChan {
   private vvcListener: any;
   constructor(vvc, vvcSession) {
      this.vvcListener = vvc.createListener(vvcSession, VVC_CONN_DUMMY_CHAN_NAME);
      if (this.vvcListener) {
         this.vvcListener.onpeeropen = (session: any, channel: any): void => {
            // always reject this channel.
            if (!vvcSession.rejectChannel(vvcSession.getChannel(channel.id))) {
               Logger.trace("failed to reject dummy channel" + " with id: " + channel.id, Logger.RDPVCBRIDGE);
               return;
            }
            channel.state = CHANNEL_STATE.CLOSED;
         };
      }
   }
   destroy(): void {}
}
/*
 * ConnectCb gets fired in about 200ms if we are connected over VVC.
 * However, components like invoke VDP_Is*Session API very early
 * in session setup and therefore, keep a reasonable timeout value here
 * for non-VVC scenarios.
 */
export const VVC_CONN_CALLBACK_TIMEOUT = 3 * 1000;
export const VVC_LISTENER_CLOSE_TIMEOUT = 3 * 1000;
export const VVC_CLOSE_ACK_TIMEOUT = 3 * 1000;

export const VVC_INTERNAL_STREAM_OPEN_TIMEOUT = 5 * 1000;
export const VVC_RDP_STREAM_OPEN_TIMEOUT = 5 * 1000;

export const VVC_SEND_TICK_INTERVAL = 1 * 1000;
export const VVC_RECV_TICK_INTERVAL = 1 * 1000;

export const VVC_OPEN_DELAY_TIMEOUT = 3 * 1000;
export const VVC_STREAM_DRAIN_TIMEOUT = 5 * 1000;

/* Forward declarations */
class VVCRecvBuffer {}
export type VVCRecvBufferQueue = Array<VVCRecvBuffer>;

class VVCSendBuffer {}
export type VVCSendBufferArray = Array<VVCSendBuffer>;
